import { environment } from '../../../environments/environment';
import { essHostOrigin, IS_ESS_HOST } from './app-host';

/**
 * Locale links. The former ESS dashboard was built per language and served under /en/... and /fr/..., so links
 * (e.g. from the mobile app) carry the language as the first path segment. Before the app starts, a leading
 * /en or /fr is turned into the session language and removed from the URL; dashboard pages are mapped to /ess.
 */
export const SUPPORTED_LANGS = ['en', 'fr'];
const LANG_STORAGE_KEY = 'lang';
const LOCALE_PREFIX = /^\/(en|fr)(?=\/|$)/;
const ESS_PREFIX = /^\/ess(?=\/|$)/;
// Pages of the former dashboard, which lived at the root of its own domain (connect is still at the root)
const LEGACY_ESS_PAGES = ['personal-data', 'communication', 'permanent-residence', 'mailing-address', 'emergency-contact',
  'login', 'message-center'];

/**
 * Rewrites the URL before the router reads it: applies the locale prefix and maps the URL to the app's hostname.
 * ESS pages opened on the New PTM hostname go to the ESS hostname (when the environment has one), and on the ESS
 * hostname /ess is dropped. Returns false when the browser is sent to the ESS hostname, so the app must not start.
 */
export function applyInitialUrl(): boolean {
  let path = window.location.pathname;
  const localeMatch = path.match(LOCALE_PREFIX);
  if (localeMatch) {
    setSessionLang(localeMatch[1]);
    path = path.slice(localeMatch[0].length) || '/';
    const firstSegment = path.split('/')[1];
    if (path === '/' || LEGACY_ESS_PAGES.includes(firstSegment)) {
      path = '/ess' + (path === '/' ? '' : path);
    }
  }

  if (ESS_PREFIX.test(path) && (IS_ESS_HOST || environment.essHost)) {
    const essPath = path.replace(ESS_PREFIX, '') || '/';
    if (!IS_ESS_HOST) {
      // the session language is per hostname, so the locale prefix goes along
      const localePrefix = localeMatch ? localeMatch[0] : '';
      window.location.replace(essHostOrigin() + localePrefix + essPath + window.location.search + window.location.hash);
      return false;
    }
    path = essPath;
  }

  if (path !== window.location.pathname) {
    window.history.replaceState(window.history.state, '', path + window.location.search + window.location.hash);
  }
  return true;
}

/** Language chosen through a locale link for this browser session, if any. */
export function getSessionLang(): string | null {
  try {
    const lang = sessionStorage.getItem(LANG_STORAGE_KEY);
    return SUPPORTED_LANGS.includes(lang) ? lang : null;
  } catch {
    return null;
  }
}

export function setSessionLang(lang: string): void {
  try {
    sessionStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // storage unavailable (private mode / blocked): the language then falls back to the browser's
  }
}
